// web/game.ts — the GAME SESSION: the bridge between the deterministic sim and
// the browser. It composes the full world the rival demo proves out (colony +
// genes + autonomy + regions + rival strategy) and designates **colony 0 as the
// player's colony**. Everything else is an autonomous rival under identical rules.
//
// PERSISTENCE MODEL (leans on the engine's determinism contract):
//   The sim is fully reproducible from (seed, params). The ONLY non-seed input is
//   the player applying a looted gene to a caste. So a save = seed + params + the
//   current tick + an ordered log of those gene actions. Loading rebuilds a fresh
//   world and REPLAYS: bulk-step to each action's tick, apply it, then step to the
//   target tick. `loop.step(n)` is byte-identical to n × step(1) (a determinism
//   test guarantees this), so replay reconstructs the exact saved state.

import {
  CASTE, CASTE_COUNT, EFFECT_CHANNEL_COUNT, GENE_TRAIT_COUNT, GENE_TYPE_COUNT, KIND, NIL,
  SPECIES_COUNT, TRAIT_FORMING, TERRITORY_STAGE_NAME, ECO_EVENT,
} from '../src/types.ts';
import type { Caste, GeneType, GeneTrait, Rarity, MutationEnergySink, Species } from '../src/types.ts';
import { TRAIT_NAME, TRAIT_DESC } from '../src/sim/personality.ts';
import { Rng } from '../src/rng.ts';
import { hashHex } from '../src/hash.ts';
import { defaultCasteConfigs, defaultColonyConfig } from '../src/sim/colonyExample.ts';
import { computeColonyDiagnostics, newPopulationSample, pushPopulationSample } from '../src/sim/diagnostics.ts';
import type { ColonyDiagnostics, PopulationSample } from '../src/sim/diagnostics.ts';
import { adviseGene } from '../src/sim/advisor.ts';
import { validCastesForType, casteCompatLabel, primaryCasteForType } from '../src/sim/casteGenes.ts';
import {
  buildEcosystemWorld, computeEcosystemStateHash, apexKindName, ecoEventName,
  defaultSpeciesConfigs, defaultEcosystemConfig, defaultEcosystemEventConfig, defaultApexConfig,
  defaultLandmarkConfig, defaultDangerAvoidanceConfig, defaultEcosystemPressureConfig,
} from '../src/sim/ecosystemExample.ts';
import type { EcosystemWorld } from '../src/sim/ecosystemExample.ts';
import { defaultDiscoveryConfig } from '../src/sim/discoveryExample.ts';
import { MORPH_NAME } from '../src/sim/discoveryData.ts';
import { defaultHiveConfig } from '../src/sim/colonyExample.ts';
import { defaultTimescaleConfig } from '../src/sim/timescale.ts';
import { SpriteCache } from '../src/sim/creatureSprite.ts';
import type { AutonomyCtx } from '../src/sim/taskQueue.ts';
import { WILDLIFE_ENCYCLOPEDIA, SPECIES_ENTRIES } from './wildlifeData.ts';
import type { WildlifeEntry } from './wildlifeData.ts';
import { BoxEconomy } from './evolutionBox.ts';
import type { ProgressSignals, OpenResult, BoxState, EnergyBreakdown } from './evolutionBox.ts';
import { GeneProgression } from './geneProgression.ts';
import type { GeneRowView, TreeRow } from './geneProgression.ts';
import { newRegionManager } from '../src/sim/regionData.ts';
import { REGION_COUNT } from '../src/sim/regions.ts';
import type { Region, RegionManager } from '../src/sim/regions.ts';
import { RivalSystem, defaultRivalConfig } from '../src/sim/rivals.ts';
import { PROJECT_DEFS, PROJECT_STATUS, PROJECT_COUNT } from '../src/sim/projectsData.ts';
import type { Project } from '../src/sim/projectsData.ts';
import { geneChannels, geneFromChannels, applyGeneStaged, GENE_IMMEDIATE_PERCENT } from '../src/sim/genome.ts';
import type { CasteGene } from '../src/sim/genome.ts';
import { effectiveTier, targetGain, scaleChannelsToBudget, projectedGainPercent } from '../src/sim/genePowerBudget.ts';
import { slotBaseline, STARTER_BASELINE } from '../src/sim/geneLootScaling.ts';
import { GENE_TYPE_NAME, RARITY_NAME, GENE_TYPE_BASE_CHANNEL } from '../src/sim/geneData.ts';
import { comparePower, genePower, genePowerOf } from '../src/sim/genePower.ts';
import { casteProfilePower, colonyPower } from './colonyPower.ts';
import type { PowerSnapshot } from './colonyPower.ts';
import { RIVAL_BAND } from '../src/sim/enemyScaling.ts';
import { TerritoryDriverSystem } from '../src/sim/territoryDriver.ts';

/** Human caste labels, indexed by CASTE.* — sourced from the canonical sim module
 *  (imported for internal use AND re-exported so existing `game.CASTE_NAME` importers work). */
import { CASTE_NAME } from '../src/sim/casteNames.ts';
export { CASTE_NAME };

/** Pressure-band labels, indexed by PRESSURE_STATE.* (Growing/Stable/OverCapacity). */
const PRESSURE_STATE_NAME: readonly string[] = ['Growing', 'Stable', 'Over capacity'];

/** The player always plays colony 0. */
export const PLAYER_COLONY = 0;

/**
 * Max stacks of a single trait carried onto a CASTE genome when applying a looted
 * gene. The gene-loot layer stacks trait counts unboundedly (autonomous splicing
 * compounds them — see rivals.ts GENE_STRENGTH_CAP), so a fully-evolved gene can
 * hold millions of stacks. Caste profiles must stay sane (a huge Movement channel
 * would explode stepsPerTick and stall the tick loop), so we clamp per-trait
 * stacks here. Bounded, deterministic, and still rewards stacked loot up to the cap.
 */
const TRAIT_STACK_CAP = 3;

/**
 * CASTE EQUIPMENT SLOTS — a WEB-LAYER gate over the genome's body-part slots.
 *
 * The sim genome (`GenomeSystem`, src/sim/genome.ts) already gives every caste 6
 * body-part slots (GENE_TYPE_COUNT), one gene per slot, stronger-replaces. This layer
 * adds a per-caste COUNT CAP on how many of those 6 slots the player may FILL — it
 * does NOT touch the sim (the base sim stays byte-identical). The cap starts at
 * `STARTING_SLOTS` and is raised +1 per `unlockSlot` action (up to GENE_TYPE_COUNT),
 * derived deterministically from the recorded action log so it survives save/replay.
 */
export const STARTING_SLOTS = 3;

/**
 * Caste-targeted (adaptive) loot tuning — the per-caste NEED weight an Evolution
 * Box biases its gene drops toward (see `casteNeedWeights`). All integer + a pure
 * function of deterministic state (slot occupancy + caste power), so the live open
 * and the replay open compute the SAME vector and the roll reproduces.
 *
 *   need(caste) = NEED_FLOOR
 *               + OPEN_W   * openSlots(caste)
 *               + DEFICIT_W * floor(powerDeficit(caste) / DEFICIT_SCALE)
 *
 * - NEED_FLOOR ≥ 1 so a "mostly complete" caste's drops are REDUCED but never zero
 *   (the spec says reduce, not eliminate — a floored caste still appears sometimes).
 * - OPEN_W dominates: a caste with an empty equipment slot is clearly favored.
 * - The power-deficit term is a secondary nudge for an underpowered caste; it is
 *   floored/divided so it can only ever be a smaller correction than a full slot.
 */
const NEED_FLOOR = 1;
const OPEN_W = 6;          // each empty equipment slot is worth ~6 (dominant signal)
const DEFICIT_W = 1;       // each DEFICIT_SCALE of power deficit adds ~1 (secondary)
const DEFICIT_SCALE = 60;  // power gap per +1 deficit weight (keeps it a nudge, not a flood)

/**
 * Escalating Mutation-Energy cost to unlock the NEXT equipment slot for a caste,
 * given how many the caste has ALREADY unlocked (0-based). Pure + deterministic so
 * the live check and replay agree. Mirrors the box/ascension spend (integer energy).
 */
export function unlockCost(unlocksSoFar: number): number {
  return 40 * (Math.max(0, unlocksSoFar) + 1);
}

/** One equipment slot for the HUD: its index, the equipped gene (or null), and state. */
export interface EquipmentSlotView {
  index: number;
  gene: { label: string; power: number; rarity: number; type: number } | null;
  state: 'filled' | 'empty' | 'locked';
}

/** A caste's full equipment layout for the HUD: the cap, occupancy, and every slot. */
export interface EquipmentLayout {
  caste: number;
  casteName: string;
  cap: number;
  occupied: number;
  max: number;
  unlockCost: number;
  canUnlock: boolean;
  slots: EquipmentSlotView[];
}

/**
 * Cap on the UI-only population-history ring buffer. Holds the most recent N
 * samples for the HUD trend graph. This is presentation state ONLY — it is never
 * part of sim state and is never folded into any state hash, so it cannot affect
 * determinism, saves, or replay (a reloaded session simply refills it as it runs).
 */
const POP_HISTORY_CAP = 96;

/** Inputs that fully determine a world (everything else is derived). */
export interface WorldParams {
  seed: number;
  width: number;
  height: number;
  colonies: number;
  startMembers: number;
  food: number;
  foodAmount: number;
  maxPerTile: number;
}

export const DEFAULT_PARAMS: WorldParams = {
  // WORLD-SCALE: the default world is ~7.4x the area of the original 56x32 tile
  // map (150x88 = 13,200 tiles vs 1,792) so travel, supply routes, and predator
  // territories all take REAL distance. Crucially, per-entity sense/speed ranges
  // are NOT scaled with the grid (see defaultCasteConfigs / defaultSpeciesConfigs)
  // — the bug stays small relative to the world, so foraging and scouting cost
  // ground. The second-order effect (colonies starving on the bigger, sparser map)
  // is corrected through endurance (startEnergy) + food supply + expansion reach,
  // never sense radius.
  seed: 1,
  width: 150,
  height: 88,
  colonies: 4,
  // Food count is deliberately BELOW area-proportional (30 -> 200, ~6.7x not 7.4x)
  // so nodes are sparser per tile -> longer supply lines -> territory matters, while
  // staying dense enough that a forager besieged by predators still finds a node and
  // nets food (measured: too-sparse food let apex sieges bankrupt founding colonies
  // into permanent extinction; this density keeps every tested seed ALIVE). Richer
  // nodes (foodAmount 14 -> 24) let those longer routes sustain themselves once a
  // forager reaches one, and give each successful deposit real weight under pressure.
  // 4 colonies on the big map start far apart, so territory and travel — not crowding
  // — drive the early game. (food/foodAmount were measure-tuned, not guessed: see the
  // per-seed viability sweep in the task notes; 200/24 was the operating point that
  // held zero extinctions AND let hive-node expansion fire across the acceptance seeds.)
  startMembers: 12,
  food: 200,
  foodAmount: 24,
  maxPerTile: 5,
};

/**
 * Recorded player actions — the only non-seed inputs. Captured by VALUE (not by
 * gene id) so replay never depends on live inventory state. Two kinds:
 *   - applyGene: splice a looted gene into a caste genome;
 *   - openBox:   open an Evolution Box (rewards are deterministic from open order).
 */
export interface ApplyGeneAction {
  tick: number;
  kind: 'applyGene';
  caste: number;
  baseType: number;
  traits: number[];
  rarity: number;
  name: string;
  restamp: boolean;
  /**
   * The applied gene's ASCENSION level at apply time (0 = un-ascended). Recorded so
   * the power-budget replay can graduate an ascended Legendary+ into the Apex band
   * via `effectiveTier(rarity, ascension)`. OPTIONAL for backward compatibility: a
   * save written before this field replays with `ascension ?? 0` ⇒ byte-identical.
   */
  ascension?: number;
}

export interface OpenBoxAction {
  tick: number;
  kind: 'openBox';
  keepDuplicates: boolean;
}

export interface FuseAction {
  tick: number;
  kind: 'fuse';
  serials: number[];
}

export interface AscendAction {
  tick: number;
  kind: 'ascend';
  serial: number;
}

/** Begin a region-scale colony project (the long-term build objective). */
export interface StartProjectAction {
  tick: number;
  kind: 'startProject';
  project: number;
}

/** Sacrifice a held gene (by stable serial) → Mutation Energy. */
export interface SacrificeAction {
  tick: number;
  kind: 'sacrifice';
  serial: number;
}

/**
 * Unlock one more equipment slot for a caste. Replay-safe: the cost is a pure
 * function of how many the caste had unlocked before (rebuilt in action order on
 * load), and the energy spend mirrors box-open / ascension (`box.spend`), so a
 * saved run reconstructs the exact caps + remaining energy.
 */
export interface UnlockSlotAction {
  tick: number;
  kind: 'unlockSlot';
  caste: number;
}

export type PlayerAction =
  | ApplyGeneAction | OpenBoxAction | FuseAction | AscendAction | StartProjectAction
  | SacrificeAction | UnlockSlotAction;

export interface LeaderRow {
  colony: number;
  isPlayer: boolean;
  score: number;
  population: number;
  military: number;
  regions: number;
  genes: number;
  evolutions: number;
  warsWon: number;
  /** The colony's emergent identity name ("Industrial", or "Forming"). */
  identity: string;
  /** True once the identity has crystallized. */
  identityEmerged: boolean;
}

/**
 * A colony's emergent identity for the HUD: the crystallized dominant trait, its
 * display name and a one-line descriptor. `emerged` is false while still forming.
 */
export interface ColonyIdentity {
  trait: number;
  name: string;
  descriptor: string;
  emerged: boolean;
}

/** A region-scale project for the HUD: its costs, live progress, rewards + gateing. */
export interface ProjectView {
  id: number;
  name: string;
  region: number;
  regionName: string;
  status: number; // PROJECT_STATUS.*
  progressPermille: number; // 0..1000 (labour+food average)
  labor: number;
  laborNeeded: number;
  food: number;
  foodCost: number;
  foodDividend: number;
  leaderboardValue: number;
  energyReward: number;
  minPopulation: number;
  blurb: string;
  canStart: boolean;
  lockReason: string;
}

export interface PlayerGeneView {
  id: number;
  label: string;
  rarity: number;
  type: number;
}

/**
 * A snapshot of the player colony's living make-up for the Hive Census panel.
 * `larvae` is the colony's brood-ready count (affordable, room-bounded brood it
 * could raise now — a derived figure, not a real larval entity). `queens` is 1
 * while the colony root stands (the persistent root IS the queen), else 0. When the
 * queen ENTITY is enabled (the playable world), she is a REAL immobile KIND.Queen unit
 * at the nest with her own HP: `queenHp`/`queenMaxHp` are her live pool (0/0 when no
 * queen entity) and `queenImperiled` flags her under heavy siege (below a quarter HP).
 */
export interface HiveCensus {
  worker: number;
  soldier: number;
  flyer: number;
  specialist: number;
  larvae: number;
  queens: number;
  queenHp: number;
  queenMaxHp: number;
  queenImperiled: boolean;
  alive: boolean;
  status: 'Thriving' | 'Recovery' | 'Rebuilding (queen only)' | 'Extinct';
}

export interface RegionView {
  region: number;
  name: string;
  glyph: string;
  unlocked: boolean;
  unlockStrength: number;
  controller: number;
  controlledByPlayer: boolean;
}

/** One bucket of the generation census: the living count born into `generation`. */
export interface GenerationBin { generation: number; count: number; }
/** A snapshot of a colony's living generation make-up (read-only; never hashed). */
export interface GenerationCensus {
  current: number;       // colony's current generation (what new offspring are born as)
  oldest: number;        // oldest LIVING generation (0 when pop 0)
  newest: number;        // newest LIVING generation (0 when pop 0)
  latestShare: number;   // % of living pop that is the `current` generation (0..100, integer)
  population: number;    // living members counted
  bins: GenerationBin[]; // live per-generation counts, ascending by generation
}

/** Live wildlife population for one species (SPECIES.*). */
export interface WildlifeCount {
  species: number;
  name: string;
  count: number;
}

/** Live apex-boss tracking snapshot (null when no boss is alive). */
export interface ApexStatus {
  alive: boolean;
  kind: number;
  name: string;
  hp: number;
  maxHp: number;
  x: number;
  y: number;
  ticksAlive: number;
  /** Colony leading the damage race (-1 / NIL when none), and its damage. */
  topThreat: number;
  topThreatDamage: number;
}

/** A recent apex sighting alert (newest last). */
export interface ApexAlert {
  kind: number;
  name: string;
  tick: number;
  x: number;
  y: number;
}

/** A resolved apex encounter — the milestones in colony history (newest last). */
export interface ApexDefeat {
  kind: number;
  name: string;
  tick: number;
  /** 'slain' (a colony beat it) or 'escaped' (it outlived its lifespan). */
  outcome: 'slain' | 'escaped';
  /** Victor colony id when slain (-1 / NIL otherwise). */
  victor: number;
  ticksAlive: number;
}

/** An encyclopedia entry joined with the creature's LIVE population count. */
export interface WildlifeEncyclopediaRow {
  entry: WildlifeEntry;
  /** Live count: species ⇒ that species' population; apex ⇒ 1 if that boss is alive. */
  liveCount: number;
}

export interface Objective {
  id: string;
  label: string;
  done: (s: GameSession) => boolean;
}

/** Onboarding goals — every check reads real, live simulation state. */
export const OBJECTIVES: readonly Objective[] = [
  { id: 'grow', label: 'Grow your colony to 15 members', done: (s) => s.colonyPopulation(PLAYER_COLONY) >= 15 },
  { id: 'loot', label: 'Loot your first gene', done: (s) => s.world.genes.countOf(PLAYER_COLONY) >= 1 },
  { id: 'apply', label: 'Apply a gene to one of your castes', done: (s) => s.appliedGeneCount() >= 1 },
  { id: 'box', label: 'Open your first Evolution Box', done: (s) => s.boxOpenedCount() >= 1 },
  { id: 'hive', label: 'Expand — found a new hive node', done: (s) => s.world.ai.hiveCountAt(PLAYER_COLONY) >= 1 },
  { id: 'region', label: 'Unlock a second region', done: (s) => s.regions.unlockedCount >= 2 },
  { id: 'war', label: 'Win a territory war', done: (s) => s.rivals.warsWonBy(PLAYER_COLONY) >= 1 },
  { id: 'project', label: 'Complete a region-scale project', done: (s) => (s.world.projects?.completedCountOf(PLAYER_COLONY) ?? 0) >= 1 },
  { id: 'lead', label: 'Climb to #1 on the leaderboard', done: (s) => s.playerRank().rank === 1 },
];

export class GameSession {
  readonly params: WorldParams;
  /**
   * The composed living world: colony + genes + autonomy + regions + the full
   * ECOSYSTEM (wildlife + dynamic events + apex bosses). `EcosystemWorld` is a
   * structural superset of `ColonyWorld`, so every existing colony read-model and
   * the colony state hash continue to work unchanged.
   */
  world!: EcosystemWorld;
  regions!: RegionManager;
  rivals!: RivalSystem;
  /** Live territory-establishment driver (provinces mature from the player's labor). */
  territoryDriver!: TerritoryDriverSystem;
  /** The player-facing Evolution Box economy (mints rewards into the inventory). */
  box!: BoxEconomy;
  /** Long-term gene progression: fusion (3→1) + named ascension ladders. */
  progression!: GeneProgression;
  /** Per-colony kill tally, fed by the engine's MutationEnergySink seam. */
  private kills!: Int32Array;
  /**
   * RENDER-ONLY population ring for the player's diagnostics TREND (a true windowed
   * slope). Caller-owned per docs/diagnostics: never folded into any hash or save, so
   * it cannot affect determinism/replay. Sampled once per advancing tick in
   * `diagnosticsInfo`; `lastPopSampleTick` gates that to one reading per tick.
   */
  private playerPopSample: PopulationSample = newPopulationSample(32);
  private lastPopSampleTick = -1;
  /**
   * Per-caste count of UNLOCKED equipment slots beyond the starting allotment
   * (sized CASTE_COUNT). Rebuilt deterministically from the recorded `unlockSlot`
   * actions in `applyActionEffect`, so a save/replay reconstructs the exact caps.
   * This is the ONLY new piece of equipment-slot state — the genome (which slots
   * are FILLED) stays the sim's authoritative source.
   */
  private slotUnlocks!: Int32Array;
  /**
   * Per (caste, GENE_TYPE) record of the LAST gene the player applied to that slot —
   * its rarity + flattened trait list — captured from the recorded `applyGene`
   * actions in `applyActionEffect`. Display-only: it lets `equipmentLayout` show a
   * filled slot's rarity colour + Power Score (the genome slot's `CasteGene` does not
   * carry rarity). Rebuilt on replay in the same order, so it is deterministic and is
   * never folded into any state hash.
   */
  private slotApplied!: ({ rarity: number; traits: number[] } | null)[][];
  /**
   * UI-only population-history ring buffer for the HUD trend graph. NOT sim state
   * and NOT hashed — `samplePopulation()` appends, capped at POP_HISTORY_CAP. A
   * fresh build starts empty and refills as the colony is played.
   */
  private popHistory: number[] = [];
  /**
   * Per-tick memo for `generationCensus`: the tick the cached snapshot was computed
   * at (per colony id) and the snapshot itself. The HUD calls `generationCensus`
   * every refresh (panel + canvas legend) for the SAME tick, and the scan is over
   * the live organism set — so memoising by (cid, tick) collapses repeated calls in
   * one tick to a single scan. Presentation-only: it is never hashed and a step
   * (which changes `this.tick`) invalidates it, so it cannot affect determinism.
   */
  private genCensusTick: number[] = [];
  private genCensusCache: GenerationCensus[] = [];
  /** Ordered log of player actions (gene applications + box opens). */
  readonly actions: PlayerAction[];

  constructor(params: WorldParams, actions: readonly PlayerAction[] = []) {
    this.params = { ...params };
    this.actions = actions.map((a) => {
      if (a.kind === 'applyGene') return { ...a, traits: [...a.traits] };
      if (a.kind === 'fuse') return { ...a, serials: [...a.serials] };
      return { ...a };
    });
    this.build();
  }

  /**
   * Construct the composed world: colony + genes + autonomy + regions + rivals,
   * now built on the FULL ecosystem (`buildEcosystemWorld`) so wildlife, dynamic
   * events, and apex bosses are live. The colony RNG draws mirror buildColonyWorld
   * exactly (wildlife/events/apex each run on their own isolated streams), so the
   * colony layer stays byte-identical; the only added inputs are stable default
   * configs, which keeps saves (seed + params + actions) reproducible on replay.
   */
  private build(): void {
    const p = this.params;
    this.world = buildEcosystemWorld({
      seed: p.seed,
      width: p.width,
      height: p.height,
      colonies: p.colonies,
      startMembers: p.startMembers,
      food: p.food,
      foodAmount: p.foodAmount,
      maxPerTile: p.maxPerTile,
      castes: defaultCasteConfigs(),
      // Enable the LARVA LIFE-STAGE for the playable world only (the shared
      // defaultColonyConfig stays instant, so every src-level determinism test is
      // byte-identical). The colony's steady reproduction now lays eggs that develop
      // for `larvaMaturation` ticks before hatching — the census "Larvae" become a
      // real, maturing brood instead of a derived headroom figure. Folded into the
      // ecosystem state hash, driven purely by sim state, so saves replay exactly.
      // `queenEntity` spawns a REAL immobile KIND.Queen at the protected player nest
      // (her own HP + visual) — playable world only, so the shared default (and every
      // src determinism test) stays byte-identical.
      // `creatureSpeed` turns the coarse integer stepsPerTick into a real per-creature
      // speed STAT (fixed-point sub-tick accumulator): a slow Size-tank moves sub-tile,
      // a Movement-evolved scout moves smoothly faster. Playable world only — the shared
      // default (and every src determinism test) stays byte-identical with it off.
      colony: { ...defaultColonyConfig(), larvaMaturation: 24, queenEntity: true, creatureSpeed: true },
      resource: { regenDelay: 18, regenAmount: 14 },
      // Stable default ecosystem configs — these MUST stay constant so a saved
      // run (seed + params + actions) replays to the identical state.
      species: defaultSpeciesConfigs(),
      ecosystem: defaultEcosystemConfig(),
      // Both layers ON so the world is genuinely alive and bosses surface to the
      // player. Omitting either would change the ecosystem hash, not the colony.
      events: defaultEcosystemEventConfig(),
      apex: defaultApexConfig(),
      // Ecosystem PRESSURE ON so wildlife around the nest actually reshapes the colony.
      // The CLI demo runs this by default, but the web world had OMITTED it — leaving the
      // pressure HUD, the queen-threat damage seam (queenEntity is on), theft of the larder,
      // the AI's forage-pull / flyer-suppress / defensive-muster adaptations, and the
      // leaderboard's REAL ecological-influence signal all dormant (the HUD read "calm" and
      // the board fell back to the food-share proxy). Pure integer scan, no RNG; folded into
      // the ecosystem hash and proven deterministic + replay-safe (ecosystemPressure.test).
      // A pre-pressure save replays to a different hash → the established graceful integrity
      // toast, the same accepted boundary as larva / hive / danger-avoidance.
      // theftLarderPermille: SIZE-RELATIVE theft — a Rat raid folds a slice of the colony's
      // STORED larder into its steal cap, so a fat, undefended stockpile draws proportionally
      // bigger raids (still bounded by the sensed theft magnitude + the store). Optional field
      // ⇒ every src determinism world (which never sets it) stays byte-identical.
      pressure: { ...defaultEcosystemPressureConfig(), theftLarderPermille: 20 },
      // Evolution DISCOVERY ON: the observer auto-discovers emergent "morph" species the
      // moment a colony's gene pool satisfies a hidden synergy. Pure + observe-only (no RNG,
      // no behaviour change) and a DERIVED view of already-hashed gene state, so it is left
      // OUT of the ecosystem hash — existing saves stay byte-identical and the archive
      // recomputes on replay. Surfaces the authoritative "morphs discovered" in the menu.
      discovery: defaultDiscoveryConfig(),
      // Named landmarks ON so the map reads as legible PLACES (food larders,
      // wildlife territories, barriers) rather than procedural noise. A stable
      // default config, so saved runs replay identically (own RNG stream).
      landmarks: defaultLandmarkConfig(),
      // Physical hive ON so the AI's founded nodes become real on-map chambers whose
      // effects run live — Storage yields a food reserve, a Military war-room garrisons
      // the nest, and the chamber count lifts the carrying-capacity ceiling. Folds into
      // the ecosystem hash; a pre-hive save replays to a different hash → graceful toast.
      hive: defaultHiveConfig(),
      // Danger-aware foraging ON: workers route AROUND spider/bird/frog territory
      // (weighted bounded A* over a per-tick predator cost field) instead of just
      // foraging closer. Deterministic; the field is a derived cache (unhashed).
      dangerAvoidance: defaultDangerAvoidanceConfig(),
      // Layered-timescale ladder ON so the major systems run on separated cadences
      // (Moment/Growth/Evolution/Civilization). The NO-ARG default uses the FIXED
      // sim reference tps — deliberately NOT settings.ticksPerSecond, which is a
      // playback knob: sim state at a tick must be playback-independent for replay.
      timescale: defaultTimescaleConfig(),
      // RIVAL PROJECTS: let non-player colonies autonomously begin a region-scale
      // project once they're established + idle, so projects are a real competitive
      // axis (not a player-only term). Deterministic (seeded, fixed colony order) so
      // saved runs replay identically; a STABLE config, like every other field here.
      projects: {
        rivalAutoStart: {
          autoStartForRivals: true,
          // Aligned to the cheapest project's own gate (Compost Network, minPop 18) so
          // a "ready" rival can actually begin one; food floor sits above the project
          // safety reserve (24) so there's surplus to invest. Rivals are power-banded to
          // the player, so an established player (project-capable) implies capable rivals.
          autoStartMinPopulation: 18,
          autoStartFoodFloor: 40,
        },
      },
    });
    // Attach a render-only sprite cache (UI/debug concern, not folded into any
    // state hash) so organisms still draw as full layered creatures (§19). The
    // ecosystem builder omits it; attaching it here keeps the sim layer untouched.
    (this.world as { sprites?: SpriteCache }).sprites =
      new SpriteCache(Math.max(this.world.genomes.snapshotCount, 64));
    this.regions = newRegionManager();
    this.rivals = new RivalSystem(
      this.world.colonies,
      this.world.genes,
      this.regions,
      new Rng(p.seed ^ 0x5bd1e995),
      this.world.colonies.capacity,
      // POWER-TARGETED enemy scaling: keep rival colonies within RIVAL_BAND of the
      // player's military strength (catch up the weak, cap the runaway) so they stay
      // relevant peers at every progression level. A completed Defense-type region
      // project ("deep sheltered galleries") also fortifies a colony's territory defence.
      {
        ...defaultRivalConfig(),
        playerColony: PLAYER_COLONY,
        powerBand: RIVAL_BAND,
        defenseBonusOf: (cid: number): number => this.world.projects?.defenseBonusOf(cid) ?? 0,
      },
    );
    // The strategic layer joins the same fixed-order tick loop.
    this.world.loop.add(this.rivals);

    // REAL leaderboard signals (closes the "two proxy score dimensions" backlog
    // item). The canonical buildEcosystemWorld leaves the leaderboard's
    // EcosystemInfluence + RegionDominance dimensions on their PROXY formulas
    // (food share / competitive strength) because the real sources live in systems
    // it doesn't own. Here — the PLAYABLE world, after the world + rivals exist — we
    // late-bind those sources so both dimensions read TRUE per-colony signals:
    //   • RegionDominance ← rivals.regionsHeldBy(cid): the colony's real region-claim
    //     count (vs the world peak), the territory supremacy the proxy stood in for.
    //   • EcosystemInfluence ← world.pressure (predation+aerial+theft absorbed): the
    //     colony's real ecological-pressure SHARE, the predation/raid signal the
    //     proxy stood in for. Only attached when the pressure layer is present.
    // DETERMINISM: both sources are pure sim state (rivals + pressure are stepped in
    // this same fixed-order loop), and all reads are integer + deterministic, so the
    // attach NEVER perturbs the RNG and a NEW save (which rebuilds the world + rivals
    // and re-attaches these same sources on replay) replays byte-identically.
    // TICK ORDER (fixed both live + replay, since this is the one tick path —
    // world.loop.step): `pressure` runs BEFORE `leaderboard` (ecosystemExample.ts
    // adds pressure then leaderboard) ⇒ same-tick-fresh influence reads; `rivals` is
    // added HERE, AFTER `leaderboard`, so the board reads PREVIOUS-tick rivals claims.
    // That ordering is identical on every run/replay, so it's deterministic — the
    // board is just consistently one tick behind on region claims, which is fine.
    // A save made BEFORE this change recorded a proxy-scored board; replaying it now
    // recomputes the board from the real signals → its leaderboard differs → the
    // established graceful integrity-hash toast fires (web/main.ts), the same accepted
    // boundary as the larva / adaptive-loot upgrades. New saves are unaffected.
    this.world.leaderboard.attachRegionDominance({
      regionsHeldOf: (cid: number): number => this.rivals.regionsHeldBy(cid),
    });
    const wp = this.world.pressure;
    if (wp) {
      this.world.leaderboard.attachEcosystemInfluence({
        influenceOf: (cid: number): number =>
          wp.predationAt(cid) + wp.aerialAt(cid) + wp.theftAt(cid),
      });
    }

    // WAR CASUALTIES → REAL ATTRITION: a lost territory war now despawns the loser's
    // bodies (not just a counter). The sink reads a colony's live roster and culls via
    // the canonical `OrganismSystem.kill` (grid/free/census + tick-stamped loot). It is
    // safe to kill here because `rivals` runs LAST in the loop — AFTER `organisms.update`
    // set `ctx.clock.tick` this tick (the loot-stamp determinism note). Roster scans are
    // in fixed activeView order ⇒ deterministic + replay-safe; the player is queen-protected
    // so attrition can never extinct it. `warCasualties` defaults to a gentle 1 per loss.
    {
      const e = this.world.entities;
      const orgs = this.world.organisms;
      this.rivals.attachCasualtySink({
        memberCount: (cid: number): number => {
          const active = e.activeView();
          const n = e.aliveCount;
          let c = 0;
          for (let i = 0; i < n; i++) {
            const id = active[i] as number;
            if ((e.kind[id] as number) === KIND.Organism && (e.colony[id] as number) === cid) c++;
          }
          return c;
        },
        memberAt: (cid: number, k: number): number => {
          const active = e.activeView();
          const n = e.aliveCount;
          let c = 0;
          for (let i = 0; i < n; i++) {
            const id = active[i] as number;
            if ((e.kind[id] as number) === KIND.Organism && (e.colony[id] as number) === cid) {
              if (c === k) return id;
              c++;
            }
          }
          return NIL; // out of range — rivals guards against this (want ≤ pool)
        },
        onWarCasualty: (_cid: number, id: number): void => { orgs.kill(id); },
      });
    }

    // Wildlife (carrying capacity) + apex (boss hp) scale to a band around the player
    // colony's POWER too — the same deterministic militaryStrength the rivals/leaderboard
    // already use. Late-bound here (after rivals exists); read in fixed tick order, so
    // saves still replay deterministically. The seam is OPTIONAL on each system, so the
    // sim core / tests that don't wire it stay byte-identical.
    const playerPower = (): number => this.rivals.militaryStrength(PLAYER_COLONY);
    this.world.ecosystem.attachPowerSource(playerPower);
    this.world.apex?.attachPowerSource(playerPower);
    // Completed PopCap-type region projects ("rafts + shoreline runs") lift a colony's
    // carrying-capacity ceiling on top of the universal per-project weight. Late-bound
    // here so the headless sim/CLI/tests (which don't wire it) stay byte-identical.
    if (this.world.projects) {
      const projects = this.world.projects;
      this.world.capacity?.attachPopCapBonus((cid: number) => projects.popCapBonusOf(cid));
    }

    // TERRITORY ESTABLISHMENT goes live: the dormant TerritorySystem now runs in the
    // fixed-order loop, driven PER-COLONY — every live colony pours its OWN establishment
    // labor + food-line throughput into its OWN slice against the rival/region
    // RegionManager, so rivals settle territory too. Each colony's established/integrated
    // provinces feed ITS OWN CARRYING-CAPACITY ceiling (a settled empire supports more
    // mouths). Added AFTER rivals so it sees the latest region unlocks; in-loop ⇒
    // deterministic + batch-invariant. Headless worlds (no driver) stay byte-identical.
    this.territoryDriver = new TerritoryDriverSystem(this.world.colonies, this.regions);
    this.world.loop.add(this.territoryDriver);
    this.world.capacity?.attachTerritory((cid: number) => this.territoryDriver.establishmentScoreOf(cid));

    // Count per-colony kills via the engine's additive MutationEnergySink seam
    // (passive — onKill only tallies; it never touches sim state, so determinism
    // and existing saves are unaffected). This feeds the player's Mutation Energy.
    this.kills = new Int32Array(this.world.colonies.capacity);
    // Equipment-slot caps: every caste starts with zero EXTRA unlocks (cap ==
    // STARTING_SLOTS); the per-slot applied-gene record starts empty. Both are
    // rebuilt by replaying the recorded actions through applyActionEffect.
    this.slotUnlocks = new Int32Array(CASTE_COUNT);
    this.slotApplied = Array.from({ length: CASTE_COUNT }, () =>
      new Array<{ rarity: number; traits: number[] } | null>(GENE_TYPE_COUNT).fill(null));
    const sink: MutationEnergySink = {
      onKill: (c: number) => {
        if (c >= 0 && c < this.kills.length) this.kills[c] = (this.kills[c] as number) + 1;
      },
    };
    const ctx = this.world.ctx as
      | (AutonomyCtx & { mutation?: MutationEnergySink; projectGeneDropPctOf?: (cid: number) => number })
      | undefined;
    if (ctx) ctx.mutation = sink;
    // Region projects of the GeneDropRate kind ("cultivated mutagens") raise a colony's
    // gene-drop rate on member deaths. Wired here (like the mutation sink) so the headless
    // sim/CLI/tests stay byte-identical; absent projects ⇒ neutral 100. Deterministic: the
    // accessor reads completed-project state, which is itself replayed from the action log.
    const projects = this.world.projects;
    if (ctx && projects) ctx.projectGeneDropPctOf = (cid: number): number => projects.geneDropPctOf(cid);

    // Player-facing Evolution Box economy over the player's gene inventory.
    this.box = new BoxEconomy(this.world.genes, this.params.seed, PLAYER_COLONY);
    // Long-term progression (fusion + ascension) over the same inventory.
    this.progression = new GeneProgression(this.world.genes, PLAYER_COLONY);
  }

  /** Build a session from a save and replay deterministically to its tick. */
  static fromSave(params: WorldParams, actions: readonly PlayerAction[], targetTick: number): GameSession {
    const s = new GameSession(params, actions);
    s.replayTo(targetTick);
    return s;
  }

  get tick(): number {
    return this.world.loop.tick;
  }

  /** Advance the live simulation by `n` ticks. */
  step(n: number): void {
    if (n > 0) this.world.loop.step(n);
  }

  // --- Player action: apply a looted gene to a caste -------------------------

  /**
   * Apply the player's looted gene `geneId` to `caste`. Records the action (by
   * value) so a later save/replay reproduces it, then applies it live. Returns a
   * short status message for the UI.
   */
  applyGene(geneId: number, caste: Caste, restamp: boolean): { ok: boolean; message: string } {
    const g = this.world.genes;
    if (!g.isAlive(geneId) || g.ownerOf(geneId) !== PLAYER_COLONY) {
      return { ok: false, message: 'That gene is no longer available.' };
    }
    const baseType = g.typeOf(geneId);
    // EQUIPMENT-SLOT CAP (web-layer gate). If this body-part slot is currently EMPTY,
    // applying the gene would FILL a new slot — reject if the caste is already at its
    // cap. A REPLACE (slot already occupied with this body-part type) never grows the
    // fill count, so it is always allowed (the sim's stronger-replaces behaviour). The
    // check runs ONLY here, in the live path; `applyActionEffect` trusts recorded
    // applies (they were valid when recorded). Slot == body-part type (genome.ts).
    const fillsNewSlot = this.world.genomes.geneAt(caste, baseType as GeneType) === null;
    if (fillsNewSlot && this.occupiedSlots(caste) >= this.slotCap(caste)) {
      const casteLabel = CASTE_NAME[caste] as string;
      return {
        ok: false,
        message:
          `${casteLabel} equipment is full (${this.occupiedSlots(caste)}/${this.slotCap(caste)}) ` +
          '— unlock a slot or replace a gene',
      };
    }
    const rarity = g.rarityOf(geneId);
    const traits = this.clampedTraitsOf(geneId);
    const name = `${RARITY_NAME[rarity] as string} ${GENE_TYPE_NAME[baseType] as string}`;
    // Capture the gene's ascension level (keyed by its stable serial) so the power-budget
    // replay can graduate an ascended Legendary+ into the Apex band. Recorded by VALUE,
    // like rarity/traits, so replay never depends on live progression state.
    const ascension = this.progression.ascensionOf(g.serialOf(geneId));
    const action: PlayerAction = {
      tick: this.tick, kind: 'applyGene', caste, baseType, traits, rarity, name, restamp, ascension,
    };
    const living = this.world.colonies.casteCountAt(PLAYER_COLONY, caste);
    this.applyActionEffect(action);
    this.actions.push(action);
    const casteName = CASTE_NAME[caste] as string;
    const who = `${living} living ${casteName}${living === 1 ? '' : 's'}`;
    const immediate = restamp
      ? `${who} mutate FULLY now`
      : `${who} feel it now (+${GENE_IMMEDIATE_PERCENT}% of full)`;
    return { ok: true, message: `${name} → ${casteName}: ${immediate}; future broods inherit it fully.` };
  }

  // --- Caste equipment slots (web-layer cap over the genome's body parts) -----

  /**
   * How many of a caste's GENE_TYPE_COUNT body-part slots the player may FILL:
   * STARTING_SLOTS + the number unlocked, clamped to the genome's hard maximum.
   */
  slotCap(caste: number): number {
    const extra = caste >= 0 && caste < CASTE_COUNT ? (this.slotUnlocks[caste] as number) : 0;
    return Math.min(GENE_TYPE_COUNT, STARTING_SLOTS + extra);
  }

  /** How many of a caste's body-part slots currently hold a gene (authoritative: the genome). */
  occupiedSlots(caste: number): number {
    const gs = this.world.genomes;
    let n = 0;
    for (let t = 0; t < GENE_TYPE_COUNT; t++) {
      if (gs.geneAt(caste as Caste, t as GeneType) !== null) n++;
    }
    return n;
  }

  /**
   * Unlock one more equipment slot for `caste` (live). Validates against the cap +
   * the player's current Mutation Energy, then records an `unlockSlot` action and
   * applies it (spending energy, raising the cap) so save/replay reproduce it.
   */
  unlockSlotAction(caste: number): { ok: boolean; message: string } {
    if (caste < 0 || caste >= CASTE_COUNT) return { ok: false, message: 'Unknown caste.' };
    const casteName = CASTE_NAME[caste] as string;
    if (this.slotCap(caste) >= GENE_TYPE_COUNT) {
      return { ok: false, message: `All ${casteName} slots unlocked` };
    }
    const cost = unlockCost(this.slotUnlocks[caste] as number);
    const current = this.box.breakdown(this.signals()).current;
    if (current < cost) {
      return { ok: false, message: `Need ${cost} Mutation Energy` };
    }
    const action: UnlockSlotAction = { tick: this.tick, kind: 'unlockSlot', caste };
    this.applyActionEffect(action);
    this.actions.push(action);
    return {
      ok: true,
      message: `Unlocked a ${casteName} equipment slot (now ${this.slotCap(caste)}/${GENE_TYPE_COUNT}).`,
    };
  }

  /**
   * The full equipment layout for a caste, for the HUD: the cap, occupancy, next
   * unlock cost + affordability, and GENE_TYPE_COUNT slot entries — the caste's
   * equipped genes first (filled), then empty slots up to the cap, then locked slots
   * beyond it. Pure read-model: it touches no sim state, draws no RNG, and is never
   * hashed/saved. A filled slot's Power Score is computed from the genome slot's
   * expressed channel vector plus the recorded rarity/traits (see `slotApplied`).
   */
  equipmentLayout(caste: number): EquipmentLayout {
    const gs = this.world.genomes;
    const cap = this.slotCap(caste);
    const occupied = this.occupiedSlots(caste);
    const cost = unlockCost(this.slotUnlocks[caste] as number);
    const current = this.box.breakdown(this.signals()).current;
    const slots: EquipmentSlotView[] = [];
    // 1) Filled slots first — one entry per occupied body-part type, genome order.
    const appliedRow = this.slotApplied[caste];
    for (let t = 0; t < GENE_TYPE_COUNT; t++) {
      const g = gs.geneAt(caste as Caste, t as GeneType);
      if (g === null) continue;
      const rec = (appliedRow ? appliedRow[t] : null) ?? null;
      slots.push({
        index: slots.length,
        gene: {
          label: g.name,
          power: this.slotGenePower(g, rec),
          rarity: rec ? rec.rarity : 0,
          type: t,
        },
        state: 'filled',
      });
    }
    // 2) Empty slots up to the cap, then 3) locked slots beyond it.
    for (let i = slots.length; i < GENE_TYPE_COUNT; i++) {
      slots.push({ index: i, gene: null, state: i < cap ? 'empty' : 'locked' });
    }
    return {
      caste,
      casteName: CASTE_NAME[caste] as string,
      cap,
      occupied,
      max: GENE_TYPE_COUNT,
      unlockCost: cost,
      canUnlock: cap < GENE_TYPE_COUNT && current >= cost,
      slots,
    };
  }

  /**
   * Power Score of an EQUIPPED gene (a genome slot's `CasteGene`). `exprPower` is the
   * L1 norm of its channel vector (its real in-sim magnitude); rarity + mutation +
   * synergy come from the recorded apply (the `CasteGene` itself carries neither). The
   * same `genePower` weighting the inventory/box reveal use, so the numbers agree.
   */
  private slotGenePower(g: CasteGene, rec: { rarity: number; traits: number[] } | null): number {
    let exprPower = 0;
    for (let c = 0; c < EFFECT_CHANNEL_COUNT; c++) {
      const v = (g.channels[c] ?? 0) as number;
      exprPower += v < 0 ? -v : v;
    }
    const traits = rec ? rec.traits : [];
    const synergy = new Set(traits).size;
    return genePower({
      rarity: rec ? rec.rarity : 0,
      mutationCount: traits.length,
      synergyCount: synergy,
      upgradeLevel: 0,
      evolutionTier: 0,
      exprPower,
    });
  }

  /** Re-apply a recorded action's effect to the live world (shared by play + replay). */
  /**
   * Begin a region-scale project — the standing long-term objective. Validated
   * against the LIVE colony (population) + region unlock so only valid starts are
   * recorded; the recorded action re-applies deterministically on replay (the sim
   * `start` re-checks population, which is deterministic). One project at a time.
   */
  startProject(project: number): { ok: boolean; message: string } {
    const projects = this.world.projects;
    if (!projects) return { ok: false, message: 'Projects are unavailable.' };
    const def = PROJECT_DEFS[project];
    if (!def) return { ok: false, message: 'Unknown project.' };
    if (projects.statusOf(PLAYER_COLONY, project as Project) === PROJECT_STATUS.Complete) {
      return { ok: false, message: `${def.name} is already complete.` };
    }
    if (projects.activeProjectOf(PLAYER_COLONY) >= 0) {
      return { ok: false, message: 'Finish your current project first — one at a time.' };
    }
    const pop = this.world.colonies.populationAt(PLAYER_COLONY);
    if (pop < def.minPopulation) {
      return { ok: false, message: `${def.name} needs a colony of ${def.minPopulation} (you have ${pop}).` };
    }
    if (!this.regions.isUnlocked(def.region as Region)) {
      return { ok: false, message: `Reach the ${this.regions.def(def.region as Region).name} region first.` };
    }
    const action: StartProjectAction = { tick: this.tick, kind: 'startProject', project };
    this.applyActionEffect(action);
    this.actions.push(action);
    return { ok: true, message: `Began ${def.name}. Your colony will build it over time.` };
  }

  private applyActionEffect(a: PlayerAction): void {
    if (a.kind === 'openBox') {
      // Replay re-rolls this open. `applyActionEffect` runs actions IN ORDER, so the
      // slot/power state here equals the live state at this open ⇒ casteNeedWeights()
      // matches the live vector ⇒ the adaptive roll reproduces byte-for-byte.
      this.box.open(a.tick, a.keepDuplicates, this.casteNeedWeights(), undefined, this.rewardBaselineFn());
      return;
    }
    if (a.kind === 'fuse') {
      this.progression.fuse(a.serials, a.tick);
      return;
    }
    if (a.kind === 'ascend') {
      this.applyAscend(a.serial);
      return;
    }
    if (a.kind === 'startProject') {
      // Deterministic start (re-checked against colony state) so replay reproduces.
      this.world.projects?.start(PLAYER_COLONY, a.project as Project);
      return;
    }
    if (a.kind === 'sacrifice') {
      // Convert a held gene (by stable serial) to banked energy. Serials are
      // deterministic, so replay sacrifices the same gene.
      this.box.sacrifice(a.serial);
      return;
    }
    if (a.kind === 'unlockSlot') {
      // Deduct the escalating cost (mirrors box-open / ascension spend) BEFORE the
      // bump so the cost is read from the pre-unlock count, then raise the cap. Both
      // are deterministic, so live play and replay agree. Recorded applies trusted
      // the cap that existed when they were recorded; replaying unlocks in order
      // rebuilds those caps exactly.
      const c = a.caste;
      if (c >= 0 && c < CASTE_COUNT) {
        this.box.spend(unlockCost(this.slotUnlocks[c] as number));
        this.slotUnlocks[c] = (this.slotUnlocks[c] as number) + 1;
      }
      return;
    }
    if (a.kind === 'applyGene') {
      // Record the applied gene's rarity + traits for the EQUIPMENT layout (so a
      // filled slot can show its rarity colour + Power Score). Display-only; the
      // genome edit below remains the authoritative slot-fill. Rebuilt on replay.
      const row = this.slotApplied[a.caste];
      if (row && a.baseType >= 0 && a.baseType < GENE_TYPE_COUNT) {
        row[a.baseType] = { rarity: a.rarity, traits: [...a.traits] };
      }
    }
    // RELATIVE POWER-PROGRESSION BUDGET (src/sim/genePowerBudget.ts). Reduce the gene to
    // its raw channel vector, then BEND that vector so the caste's power gain lands inside
    // this rarity's percent-of-current-power band — so an apply is a steady, continuous
    // step instead of a 300–500% early-game spike (or a late-game rounding error). The
    // gene keeps its identity (which stats it boosts); only its overall magnitude is
    // scaled. Deterministic: it reads the resolved (replayed) genome state + the recorded
    // rarity/traits, so the live apply and any save-replay compute the same scaled gene.
    const caste = a.caste as Caste;
    const slot = a.baseType as GeneType;
    const gs = this.world.genomes;
    const rawChannels = geneChannels(a.baseType as GeneType, a.traits as GeneTrait[], a.rarity as Rarity);
    const p0 = casteProfilePower(gs.currentProfile(caste));
    const evalDelta = (ch: readonly number[]): number =>
      casteProfilePower(gs.previewProfile(caste, slot, ch)) - p0;
    // Ascended Legendary+ genes graduate to the Apex band (effectiveTier). `?? 0` keeps
    // pre-field saves byte-identical (they replay as un-ascended, exactly as before).
    const tier = effectiveTier(a.rarity, a.ascension ?? 0);
    const target = targetGain(p0, tier, evalDelta(rawChannels));
    const scaled = scaleChannelsToBudget(rawChannels, target, evalDelta);
    const gene = geneFromChannels(a.name, slot, scaled, '');
    // Staged application: the FULL (budget-scaled) gene reaches future spawns (generational)
    // AND the living members of the caste get an IMMEDIATE partial buff (or the full gene
    // when the player opts into `restamp`) — so applying a gene feels rewarding right now.
    applyGeneStaged(
      this.world.entities, this.world.genomes, caste, gene,
      GENE_IMMEDIATE_PERCENT, a.restamp,
    );
    // Per-colony generation bump: future offspring are now a NEW generation while
    // existing units keep theirs (the same non-retroactive seam the genome edit
    // rides on). In applyActionEffect so it runs on BOTH live play and replay —
    // a save reconstructs the generation history deterministically.
    this.world.colonies.advanceGeneration(PLAYER_COLONY);
    // applyGene mutates generation state WITHOUT advancing the tick, so the per-tick
    // generationCensus memo would otherwise return a stale snapshot at this tick —
    // invalidate it so the next read recomputes.
    this.invalidateGenCensusCache();
  }

  /** Drop the per-tick generationCensus memo (a mid-tick action changed the data). */
  private invalidateGenCensusCache(): void {
    this.genCensusTick.length = 0;
    this.genCensusCache.length = 0;
  }

  /** Bulk-step to each action's tick, apply it, then step to `targetTick`. */
  private replayTo(targetTick: number): void {
    const ordered = [...this.actions].sort((a, b) => a.tick - b.tick);
    for (const a of ordered) {
      if (a.tick > targetTick) break;
      if (a.tick > this.tick) this.world.loop.step(a.tick - this.tick);
      this.applyActionEffect(a);
    }
    if (targetTick > this.tick) this.world.loop.step(targetTick - this.tick);
  }

  appliedGeneCount(): number {
    let n = 0;
    for (const a of this.actions) if (a.kind === 'applyGene') n++;
    return n;
  }

  boxOpenedCount(): number {
    return this.box.opensCount();
  }

  /** Live progression signals that feed Mutation Energy (all monotonic). */
  signals(): ProgressSignals {
    return {
      kills: this.kills[PLAYER_COLONY] as number,
      warsWon: this.rivals.warsWonBy(PLAYER_COLONY),
      hives: this.world.ai.hiveCountAt(PLAYER_COLONY),
      regionsUnlocked: this.regions.unlockedCount,
      projectsCompleted: this.world.projects?.completedCountOf(PLAYER_COLONY) ?? 0,
      // peakAt is a bounded, monotonic best-score (unlike the cumulative lifetime
      // accumulator, which can overflow Int32 to negative on a strong colony).
      standing: this.world.leaderboard.peakAt(PLAYER_COLONY),
    };
  }

  energyBreakdown(): EnergyBreakdown {
    return this.box.breakdown(this.signals());
  }

  boxState(): BoxState {
    return this.box.state(this.signals());
  }

  boxHistory(limit = 8): OpenResult[] {
    return this.box.history(limit);
  }

  /**
   * Open an Evolution Box if one is charged. Records the action (deterministic by
   * open order) and applies it live, minting rewards into the player's inventory.
   */
  openBox(keepDuplicates: boolean): { ok: boolean; message: string; result?: OpenResult } {
    const st = this.boxState();
    if (!st.ready) {
      return { ok: false, message: `Box not charged yet (${st.current}/${st.cost} energy).` };
    }
    const action: OpenBoxAction = { tick: this.tick, kind: 'openBox', keepDuplicates };
    // Caste-targeted loot: bias drops toward the colony's current caste needs. The
    // SAME vector is recomputed at the replay open above, so the roll is reproducible.
    const result = this.box.open(action.tick, keepDuplicates, this.casteNeedWeights(), undefined, this.rewardBaselineFn());
    this.annotateRewardPower(result);
    this.actions.push(action);
    return { ok: true, message: `Opened ${result.tierName} Box`, result };
  }

  /** Attach a Power comparison to each reward gene: reward vs the player's strongest
   *  held gene of the SAME type/slot (ascension-aware), so the reveal shows ▲/▼/—.
   *  Display-only — touches no sim state and is never hashed/saved. */
  private annotateRewardPower(result: OpenResult): void {
    // Evolution Advisor FIT context — the SAME read-models geneRows() uses for the
    // inventory badge, computed ONCE per open (pure, observe-only, never hashed/saved).
    const diag = this.diagnosticsInfo(PLAYER_COLONY);
    const adviceCtx = {
      bottleneck: diag.bottleneck,
      identity: this.colonyIdentity(PLAYER_COLONY).trait,
      secondaryBottlenecks: diag.secondaryBottlenecks,
    };
    const exprScratch = new Int32Array(EFFECT_CHANNEL_COUNT);
    for (const g of result.genes) {
      g.comparison = comparePower(g.power, this.bestPowerOfType(g.type, g.serial));
      // Steady, band-bounded forecast: the % this reward would add to its natural caste
      // (the relative power-progression budget). A kept reward only — a duplicate
      // converted to energy has no live gene to forecast.
      const id = g.duplicate ? NIL : this.idOfSerial(g.serial);
      if (id !== NIL) g.gainPct = this.geneApplyForecast(id, primaryCasteForType(g.type) as Caste).percent;
      // Advisor verdict on the reveal (matches the inventory badge): judge by the gene's
      // FULL expressed channel set when a live gene exists, else its base-type channel (a
      // duplicate has no live gene). So Equip / Sacrifice / Store reads FIT at a glance.
      const channels: number[] = [];
      if (id !== NIL) {
        this.world.genes.expressGene(id, exprScratch);
        for (let c = 0; c < EFFECT_CHANNEL_COUNT; c++) if ((exprScratch[c] as number) > 0) channels.push(c);
      }
      if (channels.length === 0) {
        const base = GENE_TYPE_BASE_CHANNEL[g.type];
        if (base !== undefined) channels.push(base);
      }
      g.advice = adviseGene({ type: g.type, rarity: g.rarity, channels }, adviceCtx);
    }
  }

  /** Highest Power among the player's held genes of `type`, excluding `excludeSerial`
   *  (the reward itself if it was kept), ascension included; null if none held. */
  private bestPowerOfType(type: number, excludeSerial: number): number | null {
    const inv = this.world.genes;
    let best: number | null = null;
    for (let id = inv.firstOf(PLAYER_COLONY); id !== NIL; id = inv.nextOf(id)) {
      if ((inv.typeOf(id) as number) !== type) continue;
      const serial = inv.serialOf(id) as number;
      if (serial === excludeSerial) continue;
      const p = genePowerOf(inv, id, this.progression.ascensionOf(serial));
      if (best === null || p > best) best = p;
    }
    return best;
  }

  /**
   * Pre-open SNAPSHOT of the per-slot reward baseline (geneLootScaling.slotBaseline): for
   * each gene TYPE, the player's best held gene of that type (Current Equipped Gene Power),
   * falling back to that type's natural-caste average held-gene power (Current Caste Power),
   * then the colony-wide average (Current Colony Power), then the starter floor. Read ONCE
   * before an open so a multi-gene open doesn't chase its own freshly-added rewards. Pure +
   * deterministic (reads only the replayed inventory + ascension), so the live open and any
   * save-replay derive identical baselines. See web/geneLootScaling.ts.
   */
  private rewardBaselineByType(): number[] {
    const inv = this.world.genes;
    let colonySum = 0;
    let colonyN = 0;
    const casteSum = new Array<number>(CASTE_COUNT).fill(0);
    const casteN = new Array<number>(CASTE_COUNT).fill(0);
    for (let id = inv.firstOf(PLAYER_COLONY); id !== NIL; id = inv.nextOf(id)) {
      const p = genePowerOf(inv, id, this.progression.ascensionOf(inv.serialOf(id) as number));
      colonySum += p;
      colonyN++;
      const c = primaryCasteForType(inv.typeOf(id) as number);
      if (c >= 0 && c < CASTE_COUNT) { casteSum[c] = (casteSum[c] as number) + p; casteN[c] = (casteN[c] as number) + 1; }
    }
    const colonyAvg = colonyN > 0 ? Math.round(colonySum / colonyN) : 0;
    const out = new Array<number>(GENE_TYPE_COUNT);
    for (let t = 0; t < GENE_TYPE_COUNT; t++) {
      const equipped = this.bestPowerOfType(t, -1) ?? 0;
      const c = primaryCasteForType(t);
      const casteAvg = (c >= 0 && c < CASTE_COUNT && (casteN[c] as number) > 0)
        ? Math.round((casteSum[c] as number) / (casteN[c] as number)) : 0;
      out[t] = slotBaseline(equipped, casteAvg, colonyAvg);
    }
    return out;
  }

  /** A per-type baseline lookup for `BoxEconomy.open` (snapshotted before the open). */
  private rewardBaselineFn(): (type: number) => number {
    const snap = this.rewardBaselineByType();
    return (type: number) => (type >= 0 && type < snap.length ? (snap[type] as number) : STARTER_BASELINE);
  }

  /** The gene's trait list with each stack clamped to TRAIT_STACK_CAP — the exact list
   *  `applyGene` records and the apply-budget forecast reads (so preview == result). */
  private clampedTraitsOf(geneId: number): number[] {
    const g = this.world.genes;
    const traits: number[] = [];
    for (let t = 0; t < GENE_TRAIT_COUNT; t++) {
      const cnt = Math.min(g.traitOf(geneId, t) as number, TRAIT_STACK_CAP);
      for (let k = 0; k < cnt; k++) traits.push(t);
    }
    return traits;
  }

  /** Live gene id for a stable serial among the player's held genes, or NIL. */
  private idOfSerial(serial: number): number {
    const inv = this.world.genes;
    for (let id = inv.firstOf(PLAYER_COLONY); id !== NIL; id = inv.nextOf(id)) {
      if ((inv.serialOf(id) as number) === serial) return id;
    }
    return NIL;
  }

  /**
   * Forecast applying `geneId` to `caste`: the rarity-banded PERCENT of the caste's
   * CURRENT power the apply will add — the same relative power-progression budget the
   * live apply uses (src/sim/genePowerBudget.ts), so the preview matches the result.
   * Pure read; no sim mutation, never hashed/saved. 0% for an empty/degenerate caste.
   */
  geneApplyForecast(geneId: number, caste: Caste): { percent: number; tier: number } {
    const g = this.world.genes;
    if (!g.isAlive(geneId) || g.ownerOf(geneId) !== PLAYER_COLONY) return { percent: 0, tier: 0 };
    const baseType = g.typeOf(geneId) as GeneType;
    const rarity = g.rarityOf(geneId) as number;
    const channels = geneChannels(baseType, this.clampedTraitsOf(geneId) as GeneTrait[], rarity as Rarity);
    const gs = this.world.genomes;
    const p0 = casteProfilePower(gs.currentProfile(caste));
    const rawDelta = casteProfilePower(gs.previewProfile(caste, baseType, channels)) - p0;
    return { percent: projectedGainPercent(p0, effectiveTier(rarity, 0), rawDelta), tier: effectiveTier(rarity, 0) };
  }

  /**
   * Sacrifice a held gene (by stable serial) → banked Mutation Energy. Records the
   * action so replay reproduces it, applies it live, and returns the energy gained.
   * Used by both manual reward resolution and the auto-evolution rules.
   */
  sacrificeGene(serial: number): { ok: boolean; message: string; energy: number } {
    const action: SacrificeAction = { tick: this.tick, kind: 'sacrifice', serial };
    const energy = this.box.sacrifice(action.serial);
    if (energy <= 0) return { ok: false, message: 'That gene is no longer available.', energy: 0 };
    this.actions.push(action);
    return { ok: true, message: `Sacrificed for +${energy} Mutation Energy.`, energy };
  }

  /**
   * Rich per-gene fields for loot resolution. `power` is the CANONICAL genePower
   * Score (expression + ascension aware), so the auto-rules + manual card read the
   * same number the box reveal shows. Null when the gene isn't a live owned gene.
   */
  geneInfo(id: number): { id: number; serial: number; type: number; rarity: number; traitTotal: number; power: number; label: string } | null {
    const g = this.world.genes;
    if (!g.isAlive(id) || g.ownerOf(id) !== PLAYER_COLONY) return null;
    const serial = g.serialOf(id);
    return {
      id,
      serial,
      type: g.typeOf(id),
      rarity: g.rarityOf(id),
      traitTotal: g.traitTotalOf(id),
      power: genePowerOf(g, id, this.progression.ascensionOf(serial)),
      label: g.describeGene(id),
    };
  }

  // --- Long-term gene progression: fusion + ascension ------------------------

  geneRows(): GeneRowView[] {
    const rows = this.progression.rows();
    // Evolution Advisor: compute the player's colony FIT context ONCE (the binding
    // bottleneck + emergent identity), then attach a per-gene verdict. Pure, observe-
    // only: reuses the existing diagnostics + identity read-models; no sim mutation,
    // no RNG, never hashed/saved. `channels` derives from the gene's base type
    // (GENE_TYPE_BASE_CHANNEL), matching how the inventory list already keys per type.
    const diag = this.diagnosticsInfo(PLAYER_COLONY);
    const bottleneck = diag.bottleneck;
    const secondaryBottlenecks = diag.secondaryBottlenecks;
    const identity = this.colonyIdentity(PLAYER_COLONY).trait;
    // Judge each gene by its FULL expressed channel set (base body-part channel PLUS its
    // traits), resolved via the engine's expressGene, so a trait-heavy gene gets an
    // accurate FIT verdict — not one based on its base type alone. One reused scratch
    // (this is a render read-model, not the tick path); fall back to the base channel if
    // a gene expresses nothing.
    const g = this.world.genes;
    const exprScratch = new Int32Array(EFFECT_CHANNEL_COUNT);
    for (const r of rows) {
      g.expressGene(r.id, exprScratch);
      const channels: number[] = [];
      for (let c = 0; c < EFFECT_CHANNEL_COUNT; c++) if ((exprScratch[c] as number) > 0) channels.push(c);
      if (channels.length === 0) {
        const base = GENE_TYPE_BASE_CHANNEL[r.type];
        if (base !== undefined) channels.push(base);
      }
      r.advice = adviseGene(
        { type: r.type, rarity: r.rarity, channels },
        { bottleneck, identity, secondaryBottlenecks },
      );
      // Caste compatibility (src/sim/casteGenes.ts): which castes this body part suits.
      // Pure classification of the gene's base type — no sim mutation, no RNG, not hashed.
      r.castes = validCastesForType(r.type);
      r.casteLabel = casteCompatLabel(r.type);
      // Apply forecast: the band-bounded % this gene would add to its NATURAL caste's
      // power if equipped now (the same read-model the box reveal shows). Display-only.
      r.gainPct = this.geneApplyForecast(r.id, primaryCasteForType(r.type) as Caste).percent;
    }
    return rows;
  }

  progressionTree(): TreeRow[] {
    return this.progression.tree();
  }

  /** Fuse 3 looted genes into one stronger gene (the duplicate sink). */
  fuseGenes(serials: number[]): { ok: boolean; message: string } {
    const action: FuseAction = { tick: this.tick, kind: 'fuse', serials: [...serials] };
    const r = this.progression.fuse(action.serials, action.tick);
    if (!r.ok) return { ok: false, message: r.message };
    this.actions.push(action);
    return { ok: true, message: r.message };
  }

  /** Ascend a high-tier gene one named tier — spends escalating Mutation Energy. */
  ascendGene(serial: number): { ok: boolean; message: string } {
    const cost = this.progression.ascendCost(serial);
    const current = this.box.breakdown(this.signals()).current;
    if (current < cost) {
      return { ok: false, message: `Ascension needs ${cost} Mutation Energy (you have ${Math.max(0, current)}).` };
    }
    const r = this.progression.ascend(serial);
    if (!r.ok) return { ok: false, message: r.message };
    this.box.spend(cost);
    this.actions.push({ tick: this.tick, kind: 'ascend', serial } as AscendAction);
    return { ok: true, message: r.message };
  }

  /** Apply an ascension (shared by play + replay): ascend, then spend its cost. */
  private applyAscend(serial: number): void {
    const cost = this.progression.ascendCost(serial);
    if (this.progression.ascend(serial).ok) this.box.spend(cost);
  }

  // --- Read models for the HUD ----------------------------------------------

  colonyPopulation(cid: number): number {
    return this.world.colonies.populationAt(cid);
  }

  /**
   * The player colony's current per-caste + colony POWER, read from each caste's live
   * resolved genome profile. PURE read: it touches no sim state, draws no RNG, and is
   * never hashed/saved — it only feeds the "NEW COLONY RECORD" banners (web/colonyPower.ts).
   * Uses `currentProfile(caste)` (the caste's latest resolved CasteProfile snapshot); note
   * `genomes.profile(id)` takes a snapshot ID, not a caste, so it would be wrong here.
   */
  powerSnapshot(): PowerSnapshot {
    const gs = this.world.genomes;
    const worker = casteProfilePower(gs.currentProfile(CASTE.Worker));
    const soldier = casteProfilePower(gs.currentProfile(CASTE.Soldier));
    const flyer = casteProfilePower(gs.currentProfile(CASTE.Flyer));
    const specialist = casteProfilePower(gs.currentProfile(CASTE.Specialist));
    return { worker, soldier, flyer, specialist, colony: colonyPower(worker, soldier, flyer, specialist) };
  }

  /**
   * The per-caste NEED vector (length CASTE_COUNT) an Evolution Box biases its loot
   * toward. For each caste: empty equipment slots are the DOMINANT signal (an
   * unfilled Soldier slot → more Soldier genes); a power DEFICIT vs the strongest
   * caste is a secondary nudge (an underpowered Worker → a few more Worker genes).
   * Every weight is ≥ NEED_FLOOR, so a "mostly complete" caste is REDUCED but never
   * eliminated. PURE + deterministic — it reads only slot occupancy (a function of
   * the action log) and resolved caste power (a function of the action log), both of
   * which reproduce on replay, so the live open and the replay open get an identical
   * vector and the adaptive roll is reproducible. See NEED_FLOOR/OPEN_W/DEFICIT_W.
   */
  casteNeedWeights(): number[] {
    const snap = this.powerSnapshot();
    const power = [snap.worker, snap.soldier, snap.flyer, snap.specialist];
    let maxPower = 0;
    for (let c = 0; c < CASTE_COUNT; c++) maxPower = Math.max(maxPower, power[c] as number);
    const weights: number[] = [];
    for (let c = 0; c < CASTE_COUNT; c++) {
      const open = Math.max(0, this.slotCap(c) - this.occupiedSlots(c));
      const deficit = Math.max(0, maxPower - (power[c] as number));
      weights.push(
        NEED_FLOOR + OPEN_W * open + DEFICIT_W * Math.floor(deficit / DEFICIT_SCALE),
      );
    }
    return weights;
  }

  /**
   * The caste the NEXT box will most favor (argmax of `casteNeedWeights`, ties to
   * the lowest caste index), for an optional UI hint. -1 only if the vector is empty
   * (CASTE_COUNT is always > 0, so in practice this returns a real caste). Pure read.
   */
  nextBoxFavoredCaste(): number {
    const w = this.casteNeedWeights();
    let best = -1;
    let bestW = -1;
    for (let c = 0; c < w.length; c++) {
      if ((w[c] as number) > bestW) { bestW = w[c] as number; best = c; }
    }
    return best;
  }

  /**
   * A colony's EMERGENT IDENTITY (default: the player). Reads the dominant trait
   * the personality layer has crystallized from the colony's genes, territory,
   * ecosystem kills and decisions — or a "Forming" placeholder before one emerges.
   */
  colonyIdentity(cid: number = PLAYER_COLONY): ColonyIdentity {
    const p = this.world.personality;
    const dom = p ? p.dominantOf(cid) : TRAIT_FORMING;
    const emerged = dom !== TRAIT_FORMING;
    return {
      trait: dom,
      emerged,
      name: emerged ? (TRAIT_NAME[dom] ?? 'Colony') : 'Forming',
      descriptor: emerged
        ? (TRAIT_DESC[dom] ?? '')
        : 'Its character is still taking shape — genes, territory and conflict will define it.',
    };
  }

  /**
   * Population-stability snapshot for the HUD: current population vs the colony's
   * dynamic CARRYING CAPACITY and its PRESSURE band. The foundation read-model —
   * see src/sim/capacity.ts / docs/CAPACITY.md.
   */
  capacityInfo(cid: number = PLAYER_COLONY): {
    population: number; capacity: number; pressure: number; state: number; stateName: string;
  } {
    const cap = this.world.capacity;
    const population = this.world.colonies.populationAt(cid);
    if (!cap) return { population, capacity: 0, pressure: 0, state: -1, stateName: '—' };
    const state = cap.pressureStateOf(cid);
    return {
      population,
      capacity: cap.capacityOf(cid),
      pressure: cap.pressureOf(cid),
      state,
      stateName: PRESSURE_STATE_NAME[state] ?? '—',
    };
  }

  /**
   * Wildlife PRESSURE on the colony's nest, for the HUD — the three channels the
   * ecosystem-pressure layer senses (predation / aerial / theft) plus their total. A
   * PURE, OBSERVE-ONLY read of `world.pressure` (src/sim/ecosystemPressure.ts): it does
   * not mutate the sim, draw RNG, or fold into any hash. Returns all-zero when the
   * pressure layer is absent (CLI/test worlds without `config.pressure`), so the panel
   * always has something to show.
   */
  pressureInfo(cid: number = PLAYER_COLONY): { predation: number; aerial: number; theft: number; total: number } {
    const p = this.world.pressure;
    if (!p) return { predation: 0, aerial: 0, theft: 0, total: 0 };
    const predation = Math.max(0, p.predationAt(cid));
    const aerial = Math.max(0, p.aerialAt(cid));
    const theft = Math.max(0, p.theftAt(cid));
    return { predation, aerial, theft, total: predation + aerial + theft };
  }

  /**
   * Bottleneck diagnostics for the HUD: WHY the colony has stopped growing — the
   * single binding limiter, the expected growth rate, capacity utilisation, and the
   * resource flow. A PURE, OBSERVE-ONLY read-model (src/sim/diagnostics.ts): it does
   * not mutate the sim, draw RNG, or fold into any hash. Returns a neutral snapshot
   * when the capacity layer is absent (so the panel always has something to show).
   */
  diagnosticsInfo(cid: number = PLAYER_COLONY): ColonyDiagnostics {
    const colonyCfg = defaultColonyConfig();
    // TRUE windowed trend for the player: a caller-owned population ring (render-only,
    // never hashed/saved) sampled once per advancing tick. With ≥2 readings the
    // diagnostics trend uses the real slope; before that it falls back to the inferred
    // trend (byte-identical). Rivals keep the inferred trend (no ring). The push is the
    // only side effect and touches caller-owned state only — the sim stays untouched.
    let sample: PopulationSample | undefined;
    if (cid === PLAYER_COLONY) {
      if (this.tick !== this.lastPopSampleTick && this.world.colonies.isAlive(cid)) {
        this.playerPopSample = pushPopulationSample(
          this.playerPopSample,
          this.world.colonies.populationAt(cid),
        );
        this.lastPopSampleTick = this.tick;
      }
      sample = this.playerPopSample;
    }
    return computeColonyDiagnostics(
      {
        colony: this.world.colonies,
        // capacity is optional on the world type; computeColonyDiagnostics needs a
        // reader, and the ecosystem world always wires one — assert its presence.
        capacity: this.world.capacity!,
        pressure: this.world.pressure,
        ai: this.world.ai,
        // The physical hive runs in the playable world, so feed it to the diagnostics:
        // `storageChamberCountAt` resolves "Storage vs Food at capacity" exactly (a known 0
        // ⇒ Food, ≥1 ⇒ Storage) instead of the inferred heuristic. Observe-only/hash-neutral.
        hive: this.world.hive,
        spawnCost: colonyCfg.spawnCost,
        spawnCooldown: colonyCfg.spawnCooldown,
      },
      cid,
      undefined,
      sample,
    );
  }

  /** Number of colonies that have gone extinct (for history detection). */
  extinctColonyCount(): number {
    return this.world.colonies.extinctTotal;
  }

  /** True if the player's own colony is currently extinct. */
  playerExtinct(): boolean {
    return this.world.colonies.isExtinct(PLAYER_COLONY);
  }

  /** Bitmask of currently-extinct colonies (bit cid set ⇒ colony cid extinct). */
  colonyExtinctMask(): number {
    let mask = 0;
    for (let cid = 0; cid < this.world.colonies.count; cid++) {
      if (this.world.colonies.isExtinct(cid)) mask |= 1 << cid;
    }
    return mask;
  }

  /** Cheap gene aggregate for history detection (no label building). */
  geneStats(): { count: number; maxRarity: number; typeMask: number; maxAscension: number } {
    const g = this.world.genes;
    let count = 0;
    let maxRarity = -1;
    let typeMask = 0;
    for (let id = g.firstOf(PLAYER_COLONY); id !== NIL; id = g.nextOf(id)) {
      count++;
      const r = g.rarityOf(id) as number;
      if (r > maxRarity) maxRarity = r;
      typeMask |= 1 << (g.typeOf(id) as number);
    }
    return { count, maxRarity, typeMask, maxAscension: this.progression.maxAscension() };
  }

  /** The player's strongest held gene, described (for history snapshots only). */
  topGeneLabel(): string {
    const g = this.world.genes;
    let best = NIL;
    let bestKey = -1;
    for (let id = g.firstOf(PLAYER_COLONY); id !== NIL; id = g.nextOf(id)) {
      const key = (g.rarityOf(id) as number) * 1000 + (g.traitTotalOf(id) as number);
      if (key > bestKey) { bestKey = key; best = id; }
    }
    return best === NIL ? '—' : g.describeGene(best);
  }

  /**
   * Living make-up of a colony for the Hive Census panel. The caste counts are the
   * real per-caste census; `larvae` is the count of brood actually DEVELOPING in the
   * egg→larva→adult pipeline (or, when that stage is off, the derived brood-ready
   * estimate); `queens` reflects the persistent colony root (1 while alive). `status`
   * summarises the colony:
   * extinct ⇒ gone; pop 0 but alive ⇒ rebuilding from the queen; otherwise thriving.
   */
  hiveCensus(cid: number = PLAYER_COLONY): HiveCensus {
    const c = this.world.colonies;
    const alive = c.isAlive(cid);
    const population = c.populationAt(cid);
    const status: HiveCensus['status'] = !alive
      ? 'Extinct'
      : population === 0
        ? 'Rebuilding (queen only)'
        : c.isRecovering(cid)
          ? 'Recovery'
          : 'Thriving';
    return {
      worker: c.casteCountAt(cid, CASTE.Worker),
      soldier: c.casteCountAt(cid, CASTE.Soldier),
      flyer: c.casteCountAt(cid, CASTE.Flyer),
      specialist: c.casteCountAt(cid, CASTE.Specialist),
      larvae: c.larvaCensusAt(cid),
      queens: alive ? 1 : 0,
      // Real queen-entity HP (0/0 + false when the queen entity is disabled).
      queenHp: c.queenHpAt(cid),
      queenMaxHp: c.queenMaxHp,
      queenImperiled: c.isQueenImperiled(cid),
      alive,
      status,
    };
  }

  /** The player colony's current generation (O(1); what new offspring are born as). */
  playerGeneration(cid: number = PLAYER_COLONY): number {
    return this.world.colonies.generationAt(cid);
  }

  /**
   * Living generation make-up of a colony, for the Generations HUD panel + the
   * canvas overlay legend. READ-ONLY: scans the live organism set and tallies by
   * each unit's stamped generation. NOT folded into any state hash, so it cannot
   * affect determinism or saves. `current` is the colony's current generation (what
   * new offspring are born as); oldest/newest are over LIVING gens (0 when pop 0).
   */
  generationCensus(cid: number = PLAYER_COLONY): GenerationCensus {
    // Per-tick memo: return the cached snapshot when this colony was already
    // scanned at the current tick (the HUD calls this repeatedly within one tick).
    const tick = this.tick;
    if (this.genCensusTick[cid] === tick && this.genCensusCache[cid] !== undefined) {
      return this.genCensusCache[cid] as GenerationCensus;
    }
    const e = this.world.entities;
    const colony = e.colony;
    const generation = e.generation;
    const kind = e.kind;
    const active = e.activeView();
    const n = e.aliveCount;
    const current = this.world.colonies.generationAt(cid);
    // Tally counts by generation in a small map (live gens are bounded by living pop).
    const counts = new Map<number, number>();
    let population = 0;
    for (let i = 0; i < n; i++) {
      const id = active[i] as number;
      if ((kind[id] as number) !== KIND.Organism) continue;
      if ((colony[id] as number) !== cid) continue;
      const g = generation[id] as number;
      counts.set(g, (counts.get(g) ?? 0) + 1);
      population++;
    }
    // Ascending bins; oldest/newest from the living gens (0 when none alive).
    const gens = [...counts.keys()].sort((a, b) => a - b);
    const bins: GenerationBin[] = gens.map((g) => ({ generation: g, count: counts.get(g) as number }));
    const oldest = gens.length > 0 ? (gens[0] as number) : 0;
    const newest = gens.length > 0 ? (gens[gens.length - 1] as number) : 0;
    const currentCount = counts.get(current) ?? 0;
    const latestShare = population > 0 ? Math.round((100 * currentCount) / population) : 0;
    const result: GenerationCensus = { current, oldest, newest, latestShare, population, bins };
    this.genCensusTick[cid] = tick;
    this.genCensusCache[cid] = result;
    return result;
  }

  /**
   * Push the player colony's current population onto the UI-only history buffer
   * (ring-buffered at POP_HISTORY_CAP). Called by the HUD refresh, NOT the sim, so
   * it never perturbs determinism. Sampling cadence is the HUD's, not the tick's.
   */
  samplePopulation(): void {
    this.popHistory.push(this.colonyPopulation(PLAYER_COLONY));
    if (this.popHistory.length > POP_HISTORY_CAP) this.popHistory.shift();
  }

  /** The recorded population samples (oldest → newest) for the HUD trend graph. */
  populationTrend(): readonly number[] {
    return this.popHistory;
  }

  /**
   * The player colony's home-nest tile (read-only). Used by the camera (open
   * centered on the nest, recenter button) and fog (immediate reveal on load).
   */
  playerNest(): { x: number; y: number } {
    const c = this.world.colonies;
    return { x: c.homeXAt(PLAYER_COLONY), y: c.homeYAt(PLAYER_COLONY) };
  }

  /** Snapshot of the player colony for the info panel. */
  playerInfo(): {
    population: number; food: number; genes: number; hives: number;
    military: number; regionsHeld: number; homeX: number; homeY: number;
    identity: ColonyIdentity;
    castes: { worker: number; soldier: number; flyer: number; specialist: number };
  } {
    const c = this.world.colonies;
    const cid = PLAYER_COLONY;
    return {
      population: c.populationAt(cid),
      food: c.foodAt(cid),
      genes: this.world.genes.countOf(cid),
      hives: this.world.ai.hiveCountAt(cid),
      military: this.rivals.militaryStrength(cid),
      regionsHeld: this.rivals.regionsHeldBy(cid),
      homeX: c.homeXAt(cid),
      homeY: c.homeYAt(cid),
      identity: this.colonyIdentity(cid),
      castes: {
        worker: c.casteCountAt(cid, CASTE.Worker),
        soldier: c.casteCountAt(cid, CASTE.Soldier),
        flyer: c.casteCountAt(cid, CASTE.Flyer),
        specialist: c.casteCountAt(cid, CASTE.Specialist),
      },
    };
  }

  /** The player colony's looted genes, in deterministic inventory order. */
  playerGenes(): PlayerGeneView[] {
    const g = this.world.genes;
    const out: PlayerGeneView[] = [];
    for (let id = g.firstOf(PLAYER_COLONY); id !== NIL; id = g.nextOf(id)) {
      out.push({ id, label: g.describeGene(id), rarity: g.rarityOf(id), type: g.typeOf(id) });
    }
    return out;
  }

  /** Names of every morph the world's colonies have EVER discovered (Active or Extinct) — the
   *  authoritative read of the live discovery archive, incl. extinct branches. Empty when the
   *  discovery layer isn't wired. Pure read; no sim/hash impact. */
  discoveredMorphs(): string[] {
    const d = this.world.discovery;
    if (!d) return [];
    const out: string[] = [];
    const n = d.morphCount();
    for (let m = 0; m < n; m++) if (d.isDiscovered(m)) out.push(MORPH_NAME[m] as string);
    return out;
  }

  /** Every region with unlock + control state, for the region panel. */
  regionViews(): RegionView[] {
    const out: RegionView[] = [];
    for (let r = 0; r < REGION_COUNT; r++) {
      const def = this.regions.def(r as Region);
      const controller = this.rivals.controllerOf(r as Region);
      out.push({
        region: r,
        name: def.name,
        glyph: def.glyph,
        unlocked: this.regions.isUnlocked(r as Region),
        unlockStrength: def.unlockStrength,
        controller,
        controlledByPlayer: controller === PLAYER_COLONY,
      });
    }
    return out;
  }

  /** The player's "current region": their richest held zone, else the home Backyard. */
  currentRegionName(): string {
    let best = -1;
    let bestStrength = -1;
    for (let r = 0; r < REGION_COUNT; r++) {
      if (this.rivals.controllerOf(r as Region) !== PLAYER_COLONY) continue;
      const s = this.regions.def(r as Region).unlockStrength;
      if (s > bestStrength) { bestStrength = s; best = r; }
    }
    if (best < 0) return `${this.regions.def(0 as Region).name} (home)`;
    return this.regions.def(best as Region).name;
  }

  /**
   * Strategic leaderboard, ranked by the engine's CANONICAL scorer
   * (`world.leaderboard`, the read-only `LeaderboardSystem` that runs last in the
   * tick order). The headline `score` is its `totalAt(cid)` and the row order is
   * its `rankAt(cid)` — so the HUD standings (and `playerRank()`, which the
   * "Climb to #1" objective reads) AGREE with engine scoring. The rich per-colony
   * fields (population/military/regions/genes/evolutions/warsWon) are kept verbatim
   * for the row detail/tooltip; only the ranking + headline number now come from
   * the engine. `rankAt` is a dense 0-based standing (already tie-broken cid-asc),
   * so sorting by it reproduces the engine order exactly with no extra tie-break.
   */
  leaderboard(): LeaderRow[] {
    const board = this.world.leaderboard;
    const rows: LeaderRow[] = [];
    for (let cid = 0; cid < this.world.colonies.count; cid++) {
      const military = this.rivals.militaryStrength(cid);
      const regions = this.rivals.regionsHeldBy(cid);
      const genes = this.world.genes.countOf(cid);
      const population = this.world.colonies.populationAt(cid);
      const evolutions = this.rivals.evolutionsBy(cid);
      const warsWon = this.rivals.warsWonBy(cid);
      const id = this.colonyIdentity(cid);
      rows.push({
        colony: cid, isPlayer: cid === PLAYER_COLONY,
        // Headline score = the engine's canonical ranked total for this colony.
        score: board.totalAt(cid),
        population, military, regions, genes, evolutions, warsWon,
        identity: id.name, identityEmerged: id.emerged,
      });
    }
    // Order by the engine's canonical standing (0 == leader). rankAt is already a
    // strict, tie-broken permutation, so this single key fully determines order.
    rows.sort((a, b) => board.rankAt(a.colony) - board.rankAt(b.colony));
    return rows;
  }

  playerRank(): { rank: number; of: number } {
    const lb = this.leaderboard();
    const idx = lb.findIndex((r) => r.isPlayer);
    return { rank: idx + 1, of: lb.length };
  }

  /** Every region-scale project for the player, with live progress + start gating. */
  projectViews(): ProjectView[] {
    const projects = this.world.projects;
    const out: ProjectView[] = [];
    if (!projects) return out;
    const cid = PLAYER_COLONY;
    const pop = this.world.colonies.populationAt(cid);
    const anyActive = projects.activeProjectOf(cid) >= 0;
    for (let p = 0; p < PROJECT_COUNT; p++) {
      const def = PROJECT_DEFS[p];
      if (!def) continue;
      const status = projects.statusOf(cid, p as Project);
      const regionUnlocked = this.regions.isUnlocked(def.region as Region);
      const canStart =
        status === PROJECT_STATUS.Available && !anyActive && pop >= def.minPopulation && regionUnlocked;
      let lockReason = '';
      if (status === PROJECT_STATUS.Active) lockReason = 'In progress';
      else if (status === PROJECT_STATUS.Complete) lockReason = 'Complete';
      else if (anyActive) lockReason = 'Another project active';
      else if (!regionUnlocked) lockReason = `Reach ${this.regions.def(def.region as Region).name}`;
      else if (pop < def.minPopulation) lockReason = `Needs ${def.minPopulation} pop`;
      out.push({
        id: p,
        name: def.name,
        region: def.region,
        regionName: this.regions.def(def.region as Region).name,
        status,
        progressPermille: projects.progressPermilleOf(cid, p as Project),
        labor: projects.laborOf(cid, p as Project),
        laborNeeded: def.laborNeeded,
        food: projects.foodOf(cid, p as Project),
        foodCost: def.foodCost,
        foodDividend: def.foodDividend,
        leaderboardValue: def.leaderboardValue,
        energyReward: def.energyReward,
        minPopulation: def.minPopulation,
        blurb: def.blurb,
        canStart,
        lockReason,
      });
    }
    return out;
  }

  /** Objective completion flags, in declaration order. */
  objectiveStatus(): { id: string; label: string; done: boolean }[] {
    return OBJECTIVES.map((o) => ({ id: o.id, label: o.label, done: o.done(this) }));
  }

  // --- Wildlife / ecosystem read models -------------------------------------

  /**
   * Live wildlife population per SPECIES.* (6 entries, in enum order). Reads the
   * authoritative per-species counters off the wildlife system — O(species), no
   * entity scan.
   */
  wildlifeCensus(): WildlifeCount[] {
    const w = this.world.wildlife;
    const out: WildlifeCount[] = [];
    for (let sp = 0; sp < SPECIES_COUNT; sp++) {
      out.push({
        species: sp,
        name: SPECIES_ENTRIES[sp]?.name ?? `Species ${sp}`,
        count: w.populationOf(sp as Species),
      });
    }
    return out;
  }

  /** Total live wildlife across all species. */
  wildlifeTotal(): number {
    return this.world.wildlife.population();
  }

  /**
   * The live apex boss snapshot, or null when none is alive. Pulled straight from
   * the apex system's tracking accessors (kind/hp/pos/ticks-alive/top-threat).
   */
  apexStatus(): ApexStatus | null {
    const a = this.world.apex;
    if (!a || !a.bossAlive()) return null;
    const kind = a.bossKind();
    const threat = a.topThreatColony();
    return {
      alive: true,
      kind,
      name: apexKindName(kind),
      hp: a.bossHpVal(),
      maxHp: a.bossMaxHp(),
      x: a.bossX(),
      y: a.bossY(),
      ticksAlive: a.bossTicksAlive(this.tick),
      topThreat: threat,
      topThreatDamage: threat === NIL ? 0 : a.damageBy(threat),
    };
  }

  /** Recent apex sighting alerts (oldest → newest), capped at `limit`. */
  apexAlerts(limit = 6): ApexAlert[] {
    const a = this.world.apex;
    if (!a) return [];
    const n = a.alertCount();
    const from = n > limit ? n - limit : 0;
    const out: ApexAlert[] = [];
    for (let i = from; i < n; i++) {
      const kind = a.alertKindAt(i);
      out.push({ kind, name: apexKindName(kind), tick: a.alertTickAt(i), x: a.alertXAt(i), y: a.alertYAt(i) });
    }
    return out;
  }

  /**
   * Resolved apex encounters — the milestones in colony history (oldest → newest),
   * capped at `limit`. Each is a defeat (slain by a colony) or an escape.
   */
  apexDefeats(limit = 8): ApexDefeat[] {
    const a = this.world.apex;
    if (!a) return [];
    const n = a.defeatCount();
    const from = n > limit ? n - limit : 0;
    const out: ApexDefeat[] = [];
    for (let i = from; i < n; i++) {
      const kind = a.defeatKindAt(i);
      // APEX_OUTCOME.Defeated === 0; anything else is an escape.
      const outcome: ApexDefeat['outcome'] = a.defeatOutcomeAt(i) === 0 ? 'slain' : 'escaped';
      out.push({
        kind,
        name: apexKindName(kind),
        tick: a.defeatTickAt(i),
        outcome,
        victor: a.defeatVictorAt(i),
        ticksAlive: a.defeatTicksAliveAt(i),
      });
    }
    return out;
  }

  /**
   * The static Wildlife Encyclopedia joined with LIVE counts: every species
   * (with its current population) followed by every apex boss (count 1 while
   * that boss is alive, else 0). Stable order = `WILDLIFE_ENCYCLOPEDIA`.
   */
  wildlifeEncyclopedia(): WildlifeEncyclopediaRow[] {
    const w = this.world.wildlife;
    const apex = this.world.apex;
    const liveApexKind = apex && apex.bossAlive() ? apex.bossKind() : -1;
    return WILDLIFE_ENCYCLOPEDIA.map((entry) => {
      const liveCount = entry.kind === 'species'
        ? w.populationOf(entry.id as Species)
        : (entry.id === liveApexKind ? 1 : 0);
      return { entry, liveCount };
    });
  }

  /**
   * Composite integrity fingerprint: full ECOSYSTEM state (colony + organisms +
   * wildlife + events + apex) plus the strategic rival state. Covering wildlife
   * and apex here means a save's integrity check now spans the whole living world.
   */
  hash(): string {
    return `${hashHex(computeEcosystemStateHash(this.world))}:${hashHex(this.rivals.hash())}:${hashHex(this.territoryDriver.hash())}`;
  }

  /**
   * Read-only TERRITORY ESTABLISHMENT snapshot (for the HUD): each region's current
   * stage (name + index) and percent progress to the next rung, plus the established /
   * integrated totals and the carrying-capacity bonus those provinces grant the player.
   * Pure read — touches no sim state, draws no RNG.
   */
  territoryInfo(): {
    regions: { region: number; name: string; stage: number; stageName: string; progressPct: number }[];
    establishedCount: number;
    integratedCount: number;
    capacityBonus: number;
  } {
    const t = this.territoryDriver.territory;
    const regions: { region: number; name: string; stage: number; stageName: string; progressPct: number }[] = [];
    for (let r = 0; r < REGION_COUNT; r++) {
      const stage = t.stageOf(PLAYER_COLONY, r);
      regions.push({
        region: r,
        name: this.regions.def(r as Region).name,
        stage,
        stageName: TERRITORY_STAGE_NAME[stage] as string,
        progressPct: t.progressPctToNext(PLAYER_COLONY, r),
      });
    }
    return {
      regions,
      establishedCount: t.establishedCount(PLAYER_COLONY),
      integratedCount: t.integratedCount(PLAYER_COLONY),
      capacityBonus: this.territoryDriver.establishmentScoreOf(PLAYER_COLONY),
    };
  }

  /**
   * The ACTIVE dynamic-ecosystem event (flood / heat wave / pesticide / predator
   * migration / food bloom) and how it's bending the world — so a seasonal swing in food
   * or pressure has a visible cause. Reads the live `EcosystemEventSystem`; returns null
   * when no event system is wired or nothing is active (the neutral ECO_EVENT.None). The
   * effect percentages are relative to 100 (neutral): foodRegen/wildlifeCap/geneDrop > 100
   * is a boon, < 100 a penalty; organismDrain is a flat per-tick HP bite.
   */
  eventInfo(): {
    name: string; remaining: number;
    foodRegenPct: number; wildlifeCapPct: number; geneDropPct: number; organismDrain: number;
  } | null {
    const ev = this.world.events;
    if (ev === undefined || ev.activeEvent() === ECO_EVENT.None) return null;
    return {
      name: ecoEventName(ev.activeEvent()),
      remaining: ev.remainingTicks(),
      foodRegenPct: ev.foodRegenPct(),
      wildlifeCapPct: ev.wildlifeCapacityPct(),
      geneDropPct: ev.geneDropPct(),
      organismDrain: ev.organismDrain(),
    };
  }
}
